<template>
  <v-group
    name="grid-background-group"
    :listening="false"
  >
    <v-rect v-for="item in gridBase" :key="item.id" :config="item" />
    <v-line v-for="item in gridLines" :key="item.id" :config="item" />
    <v-line v-for="tick in xTicks" :key="tick.id" :config="tick" />
    <v-line v-for="tick in yTicks" :key="tick.id" :config="tick" />
    <v-text v-for="label in rulerLabels" :key="label.id" :config="label" />
  </v-group>
</template>

<script setup>
import { performanceAttrs } from './helpers'

// todo: add an interface, required option and remove this default config
const props = defineProps({
  config: {
    type: [Object, null],
    default: null,
  },
});

const layerConfig = computed(() => props.config);

const TICK_INTERVAL = layerConfig.value.footInPx / 4;

const rulerLabels = computed(() => {
  const labels = [];
  const base = {
    x: layerConfig.value.rulerSize / 2,
    y: layerConfig.value.rulerSize / 2,
    fontSize: 12,
    fill: layerConfig.value.rulerTextColor,
    align: "center",
  };

  for (
    let i = 0;
    i <= layerConfig.value.width;
    i += layerConfig.value.footInPx
  ) {
    labels.push({
      ...base,
      id: `x-label-${i}`,
      x: layerConfig.value.rulerSize + i,
      text: ` ${i / layerConfig.value.footInPx}ft`,
    });
  }

  base.rotation = -90;

  for (
    let y = layerConfig.value.footInPx;
    y <= layerConfig.value.height;
    y += layerConfig.value.footInPx
  ) {
    labels.push({
      ...base,
      id: `y-label-${y}`,
      y: layerConfig.value.rulerSize + y,
      text: ` ${y / layerConfig.value.footInPx}ft`,
    });
  }

  return labels;
});

const xTicks = computed(() => {
  const ticks = [];

  for (
    let i = TICK_INTERVAL;
    i <= layerConfig.value.width;
    i += TICK_INTERVAL
  ) {
    const isFoot = i % layerConfig.value.footInPx === 0;
    ticks.push({
      ...performanceAttrs,
      id: `x-tick-${i}`,
      points: [
        layerConfig.value.rulerSize + i,
        isFoot ? 0 : layerConfig.value.rulerSize / 2,
        layerConfig.value.rulerSize + i,
        layerConfig.value.rulerSize,
      ],
      stroke: layerConfig.value.rulerTextColor,
      strokeWidth: isFoot ? 1 : 0.5,
    });
  }
  return ticks;
});

const yTicks = computed(() => {
  const ticks = [];

  for (
    let i = TICK_INTERVAL;
    i <= layerConfig.value.height;
    i += TICK_INTERVAL
  ) {
    const isFoot = i % layerConfig.value.footInPx === 0;
    ticks.push({
      ...performanceAttrs,
      id: `y-tick-${i}`,
      points: [
        isFoot ? 0 : layerConfig.value.rulerSize / 2,
        layerConfig.value.rulerSize + i,
        layerConfig.value.rulerSize,
        layerConfig.value.rulerSize + i,
      ],
      stroke: "#888",
      strokeWidth: isFoot ? 1 : 0.5,
    });
  }
  return ticks;
});

const gridBase = computed(() => [
  {
    name: "gridBackground",
    x: layerConfig.value.rulerSize,
    y: layerConfig.value.rulerSize,
    width: layerConfig.value.width,
    height: layerConfig.value.height,
    radius: 0,
    fill: layerConfig.value.bgColor,
    stroke: null,
    strokeWidth: 0,
    draggable: false,
  },
  {
    name: "xRulerBlock",
    x: layerConfig.value.rulerSize,
    y: 0,
    width: layerConfig.value.width,
    height: layerConfig.value.rulerSize,
    fill: "#ccc",
  },
  {
    name: "yRulerBlock",
    x: 0,
    y: layerConfig.value.rulerSize,
    width: layerConfig.value.rulerSize,
    height: layerConfig.value.height,
    fill: "#ccc",
  },
]);

const gridLines = computed(() => {
  const lines = []
  const offset = layerConfig.value.rulerSize
  const padding = layerConfig.value.padding.x
  const maxX = layerConfig.value.width
  const maxY = layerConfig.value.height
  const count = Math.ceil((maxX / padding) + (maxY / padding))
  const diffFromStart = (maxX % padding) // calculate diff to the layout be based on top left

  // todo: since we have used group clip tool,
  // we can reduce complexity of grid boundaries
  function calcEndFromSide(pos, reverse) {
    const endX = reverse ? maxX - pos : pos;

    return {
      x: Math.min(maxX, Math.max(0, endX)),
      y: Math.min(maxY, Math.max(0, pos - maxX)),
    };
  }

  function calcStartFromSide(pos, reverse) {
    let startX = reverse ? maxX : 0;

    if (pos > maxY) {
      startX = reverse ? maxX - (pos - maxY) : pos - maxY;
    }

    return {
      x: Math.max(0, Math.min(maxX, startX)),
      y: Math.max(0, Math.min(maxY, pos)),
    };
  }

  for (let i = 0; i < count; i++) {
    const pos = i * padding;
    const posDiff = i * padding + diffFromStart;
    const fromLeft = calcStartFromSide(pos, false);
    const fromRight = calcStartFromSide(posDiff, true);
    const toRight = calcEndFromSide(pos, false);
    const toLeft = calcEndFromSide(posDiff, true);

    // from LEFT to TOP
    lines.push({
      ...performanceAttrs,
      points: [
        offset + fromLeft.x,
        offset + fromLeft.y,
        offset + toRight.x,
        offset + toRight.y,
      ],
      stroke: "#ddd",
      strokeWidth: 1,
    });

    // from RIGHT to TOP
    lines.push({
      ...performanceAttrs,
      points: [
        offset + fromRight.x,
        offset + fromRight.y,
        offset + toLeft.x,
        offset + toLeft.y,
      ],
      stroke: "#ddd",
      strokeWidth: 1,
    });
  }

  return lines;
});

const emit = defineEmits(['patternUpdated'])

onMounted(() => {
  console.log(`[45-Deg Grid Pattern Mounted] with ${gridLines.value.length} lines`)
  emit('patternUpdated')
});
</script>
